/**
 * Manages agreement templates from the Documents Settings page.
 *
 * Built-in templates (SDA / NDA) are read-only — clone to customize.
 * Custom templates can be created from scratch or edited via a modal.
 * The edit modal includes a Markdown body editor and a variable list editor.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Copy, Pencil, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { useAgreementTemplates } from '@/composables/financial/useAgreements';
import type { AgreementTemplate, TemplateVariable, VariableType, VariableSource } from '@/types/table';

const KINDS = [
  { label: 'Software Development Agreement (SDA)', value: 'SDA' },
  { label: 'Non-Disclosure Agreement (NDA)',        value: 'NDA' },
  { label: 'Custom',                                value: 'CUSTOM' },
];

const VAR_TYPES: { label: string; value: VariableType }[] = [
  { label: 'Text (single line)',     value: 'text' },
  { label: 'Long text (paragraph)',  value: 'longText' },
  { label: 'Number',                 value: 'number' },
  { label: 'Currency',               value: 'currency' },
  { label: 'Percent',                value: 'percent' },
  { label: 'Date',                   value: 'date' },
  { label: 'Scope list',             value: 'scopeList' },
  { label: 'Milestone list',         value: 'milestoneList' },
];

const VAR_SOURCES: { label: string; value: VariableSource | '' }[] = [
  { label: 'Manual (user enters)',   value: 'manual' },
  { label: 'Org settings',           value: 'org' },
  { label: 'Org signatory',          value: 'orgSignatory' },
  { label: 'Org policy defaults',    value: 'orgPolicy' },
  { label: 'Selected contact',       value: 'contact' },
  { label: 'Contact\'s company',     value: 'company' },
  { label: 'Agreement field',        value: 'agreement' },
];

const templateSchema = z.object({
  kind: z.enum(['SDA', 'NDA', 'CUSTOM']),
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().max(500).optional().or(z.literal('')),
  body: z.string().min(1, 'Body is required'),
});
type TemplateFormData = z.infer<typeof templateSchema>;

export function AgreementTemplatesManager() {
  const { templates, isLoading, createTemplate, updateTemplate, removeTemplate, cloneTemplate } = useAgreementTemplates({ includeInactive: true });
  const [editing, setEditing] = useState<AgreementTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AgreementTemplate | null>(null);
  const [previewBody, setPreviewBody] = useState<{ name: string; body: string } | null>(null);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Agreement Templates</CardTitle>
            <CardDescription>
              Built-in templates can be cloned and customized. Custom templates support full body and variable editing.
            </CardDescription>
          </div>
          <Button type="button" size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />New Template
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-muted-foreground text-sm">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-6">No templates yet.</div>
          ) : (
            <div className="space-y-2">
              {templates.map((tpl) => (
                <TemplateRow
                  key={tpl.id}
                  tpl={tpl}
                  onPreview={() => setPreviewBody({ name: tpl.name, body: tpl.body })}
                  onClone={() => cloneTemplate.mutate({ id: tpl.id })}
                  onEdit={() => setEditing(tpl)}
                  onDelete={() => setConfirmDelete(tpl)}
                  onToggleActive={(v) => updateTemplate.mutate({ id: tpl.id, data: { isActive: v } })}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {(creating || editing) && (
        <TemplateEditorDialog
          open={creating || !!editing}
          mode={editing ? 'edit' : 'create'}
          template={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onCreate={(data) => createTemplate.mutate(data, { onSuccess: () => { setCreating(false); } })}
          onUpdate={(id, data) => updateTemplate.mutate({ id, data }, { onSuccess: () => setEditing(null) })}
          isPending={createTemplate.isPending || updateTemplate.isPending}
        />
      )}

      {previewBody && (
        <Dialog open={!!previewBody} onOpenChange={(o) => { if (!o) setPreviewBody(null); }}>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>{previewBody.name}</DialogTitle><DialogDescription>Raw template body (with {'{{VARIABLE}}'} placeholders)</DialogDescription></DialogHeader>
            <pre className="mt-2 max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-xs">{previewBody.body}</pre>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}
        title="Delete Template?"
        description={`Are you sure you want to delete "${confirmDelete?.name}"? If it's referenced by an active agreement, this will be blocked.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (confirmDelete) removeTemplate.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) }); }}
        isLoading={removeTemplate.isPending}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────

interface TemplateRowProps {
  tpl: AgreementTemplate;
  onPreview: () => void;
  onClone: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (next: boolean) => void;
}

function TemplateRow({ tpl, onPreview, onClone, onEdit, onDelete, onToggleActive }: TemplateRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-card p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{tpl.name}</span>
          <Badge variant="outline" className="text-[10px]">{tpl.kind}</Badge>
          {tpl.isBuiltIn && <Badge variant="secondary" className="text-[10px]">Built-in</Badge>}
          {!tpl.isActive && <Badge variant="destructive" className="text-[10px]">Inactive</Badge>}
        </div>
        {tpl.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{tpl.description}</div>}
        <div className="text-xs text-muted-foreground mt-1">{tpl.variables?.length ?? 0} variables</div>
      </div>
      <div className="flex items-center gap-1">
        <div className="flex items-center gap-2 mr-2">
          <Switch checked={tpl.isActive} onCheckedChange={onToggleActive} />
        </div>
        <Button type="button" variant="ghost" size="sm" title="Preview body" onClick={onPreview}><Eye className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" title="Clone" onClick={onClone}><Copy className="h-3.5 w-3.5" /></Button>
        {!tpl.isBuiltIn && <Button type="button" variant="ghost" size="sm" title="Edit" onClick={onEdit}><Pencil className="h-3.5 w-3.5" /></Button>}
        {!tpl.isBuiltIn && <Button type="button" variant="ghost" size="sm" title="Delete" onClick={onDelete}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

interface TemplateEditorProps {
  open: boolean;
  mode: 'create' | 'edit';
  template: AgreementTemplate | null;
  onClose: () => void;
  onCreate: (data: Partial<AgreementTemplate>) => void;
  onUpdate: (id: string, data: Partial<AgreementTemplate>) => void;
  isPending: boolean;
}

function TemplateEditorDialog({ open, mode, template, onClose, onCreate, onUpdate, isPending }: TemplateEditorProps) {
  const [variables, setVariables] = useState<TemplateVariable[]>(template?.variables ?? []);

  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      kind: (template?.kind as 'SDA' | 'NDA' | 'CUSTOM') ?? 'CUSTOM',
      name: template?.name ?? '',
      description: template?.description ?? '',
      body: template?.body ?? '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        kind: (template?.kind as 'SDA' | 'NDA' | 'CUSTOM') ?? 'CUSTOM',
        name: template?.name ?? '',
        description: template?.description ?? '',
        body: template?.body ?? '',
      });
      setVariables(template?.variables ?? []);
    }
  }, [open, template, form]);

  const onSubmit = (data: TemplateFormData) => {
    const payload: Partial<AgreementTemplate> = {
      kind: data.kind,
      name: data.name,
      description: data.description || undefined,
      body: data.body,
      variables,
    };
    if (mode === 'edit' && template) onUpdate(template.id, payload);
    else onCreate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create Template' : 'Edit Template'}</DialogTitle>
          <DialogDescription>
            Define a reusable agreement. Use <code className="text-foreground">{`{{KEY}}`}</code> placeholders in the body; each key must have a matching variable definition.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormSelect form={form} name="kind" label="Kind" options={KINDS} />
              <FormField form={form} name="name" label="Name" required />
            </div>
            <FormTextarea form={form} name="description" label="Description (optional)" />
            <div className="space-y-1">
              <Label htmlFor="body">Body (Markdown)</Label>
              <Textarea id="body" {...form.register('body')} rows={20} className="font-mono text-xs" />
              {form.formState.errors.body && <p className="text-xs text-destructive">{form.formState.errors.body.message}</p>}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Variables ({variables.length})</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setVariables([...variables, { key: 'NEW_KEY', label: 'New Variable', type: 'text', required: false }])}>
                <Plus className="mr-1 h-3.5 w-3.5" />Add Variable
              </Button>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
              {variables.length === 0 && <div className="text-xs text-muted-foreground rounded-md border border-dashed p-4 text-center">No variables yet — add one to make placeholders in the body fillable.</div>}
              {variables.map((v, i) => (
                <VariableEditor
                  key={i}
                  variable={v}
                  onChange={(next) => setVariables(variables.map((x, j) => j === i ? next : x))}
                  onDelete={() => setVariables(variables.filter((_, j) => j !== i))}
                />
              ))}
            </div>
          </div>

          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isPending}>{isPending ? 'Saving…' : (mode === 'create' ? 'Create Template' : 'Save Changes')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface VariableEditorProps {
  variable: TemplateVariable;
  onChange: (next: TemplateVariable) => void;
  onDelete: () => void;
}

function VariableEditor({ variable, onChange, onDelete }: VariableEditorProps) {
  return (
    <div className="rounded-md border bg-muted/20 p-2.5 space-y-2 text-xs">
      <div className="flex items-center gap-1">
        <Input value={variable.key}    onChange={(e) => onChange({ ...variable, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} placeholder="VAR_KEY" className="h-7 font-mono" />
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onDelete}><Trash2 className="h-3 w-3 text-destructive" /></Button>
      </div>
      <Input value={variable.label} onChange={(e) => onChange({ ...variable, label: e.target.value })} placeholder="Label" className="h-7" />
      <div className="grid grid-cols-2 gap-1">
        <select
          className="h-7 w-full rounded-md border bg-background px-2 text-xs"
          value={variable.type}
          onChange={(e) => onChange({ ...variable, type: e.target.value as VariableType })}
        >
          {VAR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          className="h-7 w-full rounded-md border bg-background px-2 text-xs"
          value={variable.source ?? 'manual'}
          onChange={(e) => onChange({ ...variable, source: (e.target.value || undefined) as VariableSource | undefined })}
        >
          {VAR_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={variable.required} onChange={(e) => onChange({ ...variable, required: e.target.checked })} />
        Required
      </label>
    </div>
  );
}
